import { Component, ViewChild, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { AppMatAutocompleteComponent, XmedicalUiModule } from '@xmedical/ui';
import { Problem, PipesModule } from '@xmedical/core';
import { WithReferralInfo } from '../../types/referral-info';
import { ProblemListViewComponent } from './problem-list-view.component';

/**
 * Generic interface for displayed problem items.
 */
export interface ProblemDisplayItem extends WithReferralInfo {
  id?: number;
  problem: string;
  comment?: string;
  lastModifiedDate?: Date;
  lastModifiedUser?: { firstName?: string; surname?: string };
}

/**
 * Event emitted when a problem is added from search results.
 */
export interface ProblemAddedEvent {
  item: Problem;
  comment: string;
}

/**
 * Event emitted when a problem is removed from the list.
 */
export interface ProblemRemovedEvent {
  item: ProblemDisplayItem;
  index: number;
}

@Component({
  selector: 'xm-problem-widget-view',
  templateUrl: './problem-widget-view.component.html',
  styleUrls: ['./problem-widget-view.component.scss'],
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    XmedicalUiModule,
    PipesModule,
    ProblemListViewComponent
  ]
})
export class ProblemWidgetViewComponent {
  @ViewChild(AppMatAutocompleteComponent) autocomplete!: AppMatAutocompleteComponent;

  // Inputs
  items = input<ProblemDisplayItem[]>([]);
  title = input<string>('Problem List');
  searchFn = input.required<(query: string) => Observable<Problem[]>>();
  isProcessing = input<boolean>(false);
  canAdd = input<boolean>(true);
  canDelete = input<boolean>(true);
  addItemText = input<string>('Add new problem');
  usePrimaryText = input<boolean>(false);

  // Outputs
  itemAdded = output<ProblemAddedEvent>();
  itemRemoved = output<ProblemRemovedEvent>();
  addNewItem = output<string>();

  // Track comments for search results before adding
  resultComments: { [key: number]: string } = {};

  getResultComment(itemId: number): string {
    return this.resultComments[itemId] || '';
  }

  setResultComment(itemId: number, comment: string): void {
    this.resultComments[itemId] = comment;
  }

  onItemSelected(result: Problem): void {
    const comment = this.getResultComment(result.id);
    this.itemAdded.emit({ item: result, comment });
    this.clearSearch();
  }

  onRemoveItem(item: ProblemDisplayItem, index: number): void {
    this.itemRemoved.emit({ item, index });
  }

  onAddNewItem(): void {
    const searchTerm = this.autocomplete?.queryText || '';
    this.clearSearch();  // Close search panel
    this.addNewItem.emit(searchTerm);
  }

  private clearSearch(): void {
    this.resultComments = {};
    if (this.autocomplete) {
      this.autocomplete.cancelSearch();
    }
  }

}
